import type { BlockShapeDividerEdge, BlockShapeDividers } from '@sites/entity-core';
import {
    blockShapeDividerAt,
    blockShapeDividerColor,
    blockShapeDividerPatternWidth,
    DEVICES,
} from '@sites/entity-core';

import {
    BOTTOM_SHAPE_DIVIDERS,
    TOP_SHAPE_DIVIDERS,
    FLIP_HORIZONTAL_TOP_SHAPE_DIVIDERS,
    FLIP_HORIZONTAL_BOTTOM_SHAPE_DIVIDERS,
    FLIP_HORIZONTAL_VERTICAL_BOTTOM_SHAPE_DIVIDERS,
    FLIP_HORIZONTAL_VERTICAL_TOP_SHAPE_DIVIDERS,
    FLIP_VERTICAL_BOTTOM_SHAPE_DIVIDERS,
    FLIP_VERTICAL_TOP_SHAPE_DIVIDERS,
} from '@common/constants/shape-dividers.const';
import { EBlockShapeDividerFlip } from '@common/enums/block-shape-dividers-type.enum';

/**
 * One rendered divider: an edge at a breakpoint. Both apps build the same list
 * and render it with one loop — the SVG used to be pasted out four times in the
 * editor and four more in the builder, each copy reading its own bag.
 *
 * The shapes themselves live in `@common/constants` as pre-flipped markup, which
 * is why the lookup stays in the app rather than moving into the package with
 * the rest of the block's styling.
 */
export type ShapeDividerSlot = {
    /** Stable per block, edge and breakpoint — the SVG's internal refs use it. */
    id: string;
    cssClass: string;
    fill: string;
    height: number;
    patternWidth: string;
    markup: string;
};

type FlipKey = '' | 'h' | 'v' | 'hv';

const SHAPES: Record<BlockShapeDividerEdge, Record<FlipKey, string[]>> = {
    top: {
        '': TOP_SHAPE_DIVIDERS,
        h: FLIP_HORIZONTAL_TOP_SHAPE_DIVIDERS,
        v: FLIP_VERTICAL_TOP_SHAPE_DIVIDERS,
        hv: FLIP_HORIZONTAL_VERTICAL_TOP_SHAPE_DIVIDERS,
    },
    bottom: {
        '': BOTTOM_SHAPE_DIVIDERS,
        h: FLIP_HORIZONTAL_BOTTOM_SHAPE_DIVIDERS,
        v: FLIP_VERTICAL_BOTTOM_SHAPE_DIVIDERS,
        hv: FLIP_HORIZONTAL_VERTICAL_BOTTOM_SHAPE_DIVIDERS,
    },
};

const EDGES: readonly BlockShapeDividerEdge[] = ['top', 'bottom'];

/** Anything other than the two flip literals reads as "not flipped". */
const flipKey = (horizontal: string | undefined, vertical: string | undefined): FlipKey =>
    `${horizontal === EBlockShapeDividerFlip.HORIZONTAL ? 'h' : ''}${
        vertical === EBlockShapeDividerFlip.VERTICAL ? 'v' : ''
    }` as FlipKey;

export function getShapeDividerSlots(block: {
    id: string;
    props: { shapeDividers?: BlockShapeDividers };
}): ShapeDividerSlot[] {
    const slots: ShapeDividerSlot[] = [];

    DEVICES.forEach((device) => {
        EDGES.forEach((edge) => {
            const divider = blockShapeDividerAt(block.props?.shapeDividers, edge, device);

            if (!divider?.isEnabled) {
                return;
            }

            // `view` is a 1-based index into the shape tables.
            const markup =
                SHAPES[edge][flipKey(divider.horizontalFlip, divider.verticalFlip)][Number(divider.view) - 1];

            slots.push({
                id: `${device}-${edge}-divider_${block.id}`,
                cssClass: `block-shape-divider is-${edge} for-${device}`,
                fill: blockShapeDividerColor(divider.color),
                height: divider.height,
                patternWidth: blockShapeDividerPatternWidth(divider.repeat),
                markup,
            });
        });
    });

    return slots;
}
